import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, type Theme } from "@mui/material/styles";
import { styled } from "../../../../lib/styled";
import type { PubmedQueryPreview } from "@shared/types/corpus/api.ts";
import { Caption } from "../../../primitives/data-display/Caption";
import { Note } from "../../../primitives/data-display/Note";
import { SectionTitle } from "../../../primitives/data-display/SectionTitle";
import { gaps } from "../../../primitives/spacing";
import { Panel } from "../../../primitives/surfaces/Panel";
import { DocumentLink } from "../DocumentLink";

type Tone = "primary" | "warning" | "error";

const Callout = styled("div")<{ $tone: Tone }>(
  ({ theme, $tone }: { theme: Theme; $tone: Tone }) => ({
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette[$tone].main, 0.12),
  })
);

const Matches = styled("ul")(({ theme }) => ({
  margin: 0,
  padding: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
}));

const Warnings = styled("ul")({ margin: 0, paddingLeft: "1.25em" });

export interface FeedQueryPreviewProps {
  /** What PMC said about the query; null while the query has not been validated. */
  preview: PubmedQueryPreview | null;
  /** The most papers one run takes. */
  runCap: number;
  /** The most papers a feed's query may match when the feed is created. */
  matchLimit: number;
}

function toneOf(count: number, matchLimit: number): Tone {
  if (count > matchLimit) return "error";
  return count === 0 ? "warning" : "primary";
}

function describeRuns(count: number, runCap: number, matchLimit: number): string {
  if (count > matchLimit)
    return `Over the limit at creation: ${matchLimit.toLocaleString()}. Narrow the query.`;
  if (count === 0) return "Nothing matches yet. The feed picks up papers as they are published.";
  const limit = `Limit at creation: ${matchLimit.toLocaleString()}.`;
  if (count <= runCap) return `The first run takes them all. ${limit}`;
  return `First run takes ${runCap.toLocaleString()}, the next ${(count - runCap).toLocaleString()}. ${limit}`;
}

/** What a query would bring a feed, shown before the feed exists: how many papers match, how the
 *  runs would take them, a sample of the matches and anything NCBI flagged about the query. */
export function FeedQueryPreview({ preview, runCap, matchLimit }: FeedQueryPreviewProps) {
  return (
    <Panel>
      <Stack gap={gaps.base}>
        <SectionTitle>Preview</SectionTitle>
        {preview === null ? (
          <Note>Validate the query to see how many papers it matches.</Note>
        ) : (
          <>
            <Callout $tone={toneOf(preview.count, matchLimit)}>
              <Typography variant="h5" component="div">
                {preview.count.toLocaleString()}{" "}
                <Typography variant="body1" component="span" color="text.secondary">
                  matching papers
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {describeRuns(preview.count, runCap, matchLimit)}
              </Typography>
            </Callout>
            {preview.warnings.length > 0 && (
              <Alert severity="warning">
                <Warnings>
                  {preview.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </Warnings>
              </Alert>
            )}
            {preview.sample.length > 0 && (
              <Stack gap={gaps.snug}>
                <Caption>Sample matches</Caption>
                <Matches>
                  {preview.sample.map((paper) => (
                    <li key={paper.url}>
                      <DocumentLink title={paper.title} url={paper.url} />
                    </li>
                  ))}
                </Matches>
                {preview.count > preview.sample.length && (
                  <Caption>
                    + {(preview.count - preview.sample.length).toLocaleString()} more
                  </Caption>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Panel>
  );
}
